<template>
  <div class="flex flex-col justify-start items-center md:items-start w-full h-full overflow-y-auto px-2 pt-4 md:px-8 md:pt-4 pb-10">
    <div class="w-full max-w-[1100px] flex flex-col gap-4">
      <!-- Desktop header -->
      <div v-if="!isMobile">
        <h2 class="text-2xl font-medium text-gray-800">ניתוחי מידע</h2>
        <p class="text-sm text-surface-500 mt-1">סקירת הביצועים של העסק — תורים, לקוחות והכנסות בתקופה הנבחרת.</p>
      </div>

      <!-- Date range filter -->
      <AnalyticsDateFilter @change="onRangeChange"/>

      <!-- Content -->
      <BusinessOverviewTab
        v-if="dashboard && !isLoading"
        :data="dashboard"
        :periodLabel="periodLabel"
        :showPaymentsCard="hasPaymentsAccess"
        @ask="openChat"
        @questions="qs => suggestedQuestions = qs"
        @goPayments="goTo(AdminRouteNames.Payments)"/>

      <!-- Loading -->
      <div v-if="isLoading && !isErrorState" class="flex flex-col gap-3 w-full py-4">
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-3">
          <Skeleton class="lg:col-span-3" height="10rem" borderRadius="1.25rem"/>
          <Skeleton class="lg:col-span-2" height="10rem" borderRadius="1.25rem"/>
        </div>
        <Skeleton height="14rem" borderRadius="1.25rem"/>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Skeleton v-for="n of 4" :key="n" height="12rem" borderRadius="1.25rem"/>
        </div>
      </div>

      <!-- Error -->
      <retry-loader v-else-if="isErrorState" :isErrorState="isErrorState" @retry-function="fetchData"/>

      <!-- Export docs -->
      <div v-if="!isErrorState" v-wave class="card flex items-center gap-3 cursor-pointer" @click="openExportDocs">
        <span class="w-11 h-11 rounded-[13px] shrink-0 flex items-center justify-center" style="background:#edeafd">
          <i class="pi pi-download text-lg" style="color:#6831d0"/>
        </span>
        <span class="flex-1 min-w-0">
          <span class="block text-[14px] font-bold text-gray-900">ייצוא מסמכים</span>
          <span class="block text-[11.5px] text-gray-500 mt-0.5">יומן תורים · יומן שינויים · קבצים לביקורת מס</span>
        </span>
        <i class="pi pi-chevron-left text-sm text-gray-400"/>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import Skeleton from 'primevue/skeleton';
import {useDrawer} from '@torim-app/shared-components';

import AnalyticsDateFilter from '@app/components/admin/analytics/AnalyticsDateFilter.vue';
import BusinessOverviewTab from '@app/components/admin/analytics/BusinessOverviewTab.vue';
import AnalyticsAiChatDrawer from '@app/components/admin/analytics/AnalyticsAiChatDrawer.vue';
import ExportDocsSheet from '@app/components/admin/pages/reports/ExportDocsSheet.vue';

import {useAdminStore} from '@app/stores';
import {useResponsive} from '@app/composables/responsive';
import {AdminRouteNames} from '@app/router/route-names';
import {AnalyticsApi} from '@app/api/admin';
import type {IAnalyticsDashboard} from '@torim-app/types';
import {useNavigation} from '@app/composables/navigation';
import {trackEvent} from '@app/composables/posthog';

const adminStore = useAdminStore();
const {setPage} = adminStore;
const {goTo} = useNavigation();
const {openDrawer} = useDrawer();
const {isMobile} = useResponsive();

// Non-payment businesses (no clearing and no invoicing) don't get the payments
// cross-link — mirrors how the "כספים וסליקה" page itself is hidden from the menu.
const hasPaymentsAccess = computed(() => {
  const integration = adminStore.getBusinessSiteData?.paymentIntegration;
  return !!integration?.paymentConfig?.provider || !!integration?.invoiceConfig?.provider;
});

const dashboard = ref<IAnalyticsDashboard | null>(null);
const isLoading = ref(true);
const isErrorState = ref(false);

const range = ref<{ startDate: string; endDate: string }>({startDate: '', endDate: ''});
const periodLabel = ref('החודש');
const suggestedQuestions = ref<string[]>([]);

const fetchData = async () => {
  if (!range.value.startDate || !range.value.endDate) return;
  try {
    isErrorState.value = false;
    isLoading.value = true;
    dashboard.value = await AnalyticsApi.fetchDashboard(range.value);
  } catch (e) {
    console.error(e);
    isErrorState.value = true;
  } finally {
    isLoading.value = false;
  }
};

const onRangeChange = (newRange: { startDate: string; endDate: string; label?: string }) => {
  range.value = {startDate: newRange.startDate, endDate: newRange.endDate};
  if (newRange.label) periodLabel.value = newRange.label;
  fetchData();
};

// Free-text questions open a chat session in a drawer — the session state lives
// inside the drawer component, so closing the drawer ends it.
const openChat = (question: string) => {
  openDrawer(AnalyticsAiChatDrawer, {
    initialMessage: question,
    suggestedQuestions: suggestedQuestions.value,
  }, {title: 'צ׳אט AI', noPadding: true});
};

const openExportDocs = () => {
  trackEvent('docs_export_sheet_opened');
  openDrawer(ExportDocsSheet, {
    startDate: range.value.startDate,
    endDate: range.value.endDate,
    periodLabel: periodLabel.value,
  }, {title: 'ייצוא מסמכים'});
};

onMounted(() => {
  setPage(AdminRouteNames.Analytics);
  trackEvent('analytics_viewed');
});
</script>

<style scoped lang="scss">

.card {
  background: #fff;
  border: 1px solid rgb(228 228 231);
  border-radius: 18px;
  padding: 14px 15px;
}
</style>
